import { useEffect, useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SelectOption } from './SelectField';

/**
 * A multi-select shown as removable chips plus an "Add" menu.
 *
 * Chips rather than a grid of checkboxes because these fields arrive
 * pre-filled: the user's job is to remove what doesn't apply, and a chip with
 * an × makes that the obvious gesture.
 */
export default function TagField({
  values,
  options,
  onChange,
  ariaLabel,
}: {
  values: string[];
  options: SelectOption[];
  onChange: (values: string[]) => void;
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocument = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocument);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocument);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const remaining = options.filter((o) => !values.includes(o.value));
  const labelFor = (value: string) => options.find((o) => o.value === value)?.label ?? value;

  return (
    <div
      className="flex min-h-[42px] flex-wrap items-center gap-1.5 rounded-lg border bg-muted/40 p-2"
      role="group"
      aria-label={ariaLabel}
    >
      {values.map((value) => (
        <span
          key={value}
          className="inline-flex items-center gap-1.5 rounded-full border bg-card py-1 pl-3 pr-1.5 text-[12.5px] font-medium text-foreground"
        >
          {labelFor(value)}
          <button
            type="button"
            aria-label={`Remove ${labelFor(value)}`}
            onClick={() => onChange(values.filter((v) => v !== value))}
            className="flex size-5 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <X className="size-3.5" />
          </button>
        </span>
      ))}

      <div ref={rootRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-dashed px-3 py-1 text-[12.5px] font-semibold text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
        >
          <Plus className="size-3" />
          Add
        </button>
        {open && (
          <div className="absolute left-0 top-[calc(100%+6px)] z-30 max-h-[230px] min-w-[240px] overflow-y-auto rounded-xl border bg-popover p-1.5 shadow-lg">
            {remaining.length === 0 ? (
              <p className="p-2.5 text-center text-[12.5px] text-muted-foreground">
                Nothing left to add
              </p>
            ) : (
              remaining.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange([...values, option.value]);
                    setOpen(false);
                  }}
                  className={cn(
                    'block w-full cursor-pointer rounded-md px-2.5 py-2 text-left text-[13px]',
                    'text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
                  )}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
